import {
  StoryProgress,
  TUTORIAL_TRACK_IDS,
  TutorialTrackId,
  TutorialTrackProgress,
} from "../../game/GameState";
import { StorySceneId } from "../../game/story";
import { trackSteps, TutorialStepId } from "../../game/tutorial";

/**
 * The funnel's rungs: which story scenes have played and how far each
 * tutorial track's walk has gone. Both are one-way — a scene stays
 * played, the walk only moves forward — so the difference between two
 * readings of the ledger is exactly the set of milestones reached
 * between them, each at most once per playthrough.
 */
export interface MilestoneLedger {
  readonly scenes: ReadonlySet<StorySceneId>;
  /** Each track's walk index — the number of steps it has passed. */
  readonly steps: Readonly<Record<TutorialTrackId, number>>;
}

export type Milestone =
  | { readonly kind: "scene"; readonly id: StorySceneId }
  | {
      readonly kind: "step";
      readonly track: TutorialTrackId;
      readonly id: TutorialStepId;
    };

export const EMPTY_LEDGER: MilestoneLedger = {
  scenes: new Set(),
  steps: { opening: 0, firstNight: 0, dust: 0 },
};

/** Read the ledger off the shop's story and tutorial records. */
export function ledgerOf(
  story: StoryProgress,
  tutorials: Readonly<Record<TutorialTrackId, TutorialTrackProgress>>,
): MilestoneLedger {
  return {
    scenes: new Set(story.scenesPlayed as ReadonlyArray<StorySceneId>),
    steps: Object.fromEntries(
      TUTORIAL_TRACK_IDS.map((track) => [track, tutorials[track].step]),
    ) as Record<TutorialTrackId, number>,
  };
}

/**
 * Every milestone in `now` that `seen` didn't have: scenes in the order
 * the story lists them, then each track's newly passed steps in walk
 * order.
 */
export function newMilestones(
  seen: MilestoneLedger,
  now: MilestoneLedger,
): Milestone[] {
  const reached: Milestone[] = [];
  for (const id of now.scenes) {
    if (!seen.scenes.has(id)) reached.push({ kind: "scene", id });
  }
  for (const track of TUTORIAL_TRACK_IDS) {
    const steps = trackSteps(track);
    const from = Math.min(seen.steps[track], steps.length);
    const to = Math.min(now.steps[track], steps.length);
    for (let i = from; i < to; i++) {
      reached.push({ kind: "step", track, id: steps[i].id });
    }
  }
  return reached;
}
